/**
 * BacklogItemDetail Component
 * AIDLC Administrative Backend
 *
 * Full detail modal for a backlog item. Supports:
 * - View/edit title, description, priority, MoSCoW, status
 * - Assign to agent
 * - Alert preferences (in-app, email, SMS, push)
 * - Comments thread with add comment
 * - "Discuss with Agent" button to open chat
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../shared/Modal';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { updateBacklogItem, addBacklogComment, generateEstimate } from '../../services/backlogService';
import { getAllAgents } from '../../services/agentService';
import type { BacklogItem, BacklogMoscow, BacklogPriority, BacklogStatus, AlertPrefs, TokenEstimate } from '../../types/backlog';

interface BacklogItemDetailProps {
  item: BacklogItem;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

const moscowLabels: Record<string, string> = {
  must: 'Must Have',
  should: 'Should Have',
  could: 'Could Have',
  wont: "Won't Have",
};

const statusLabels: Record<string, string> = {
  new: 'New', open: 'Open', in_progress: 'In Progress',
  review: 'Review', done: 'Done', closed: 'Closed',
};

const priorityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-600',
};

interface AgentOption { id: string; name: string }

export function BacklogItemDetail({ item, isOpen, onClose, onUpdated }: BacklogItemDetailProps) {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [priority, setPriority] = useState<BacklogPriority>(item.priority);
  const [moscow, setMoscow] = useState<BacklogMoscow | ''>(item.moscow || '');
  const [status, setStatus] = useState<BacklogStatus>(item.status);
  const [assignedAgent, setAssignedAgent] = useState(item.assignedAgent || '');
  const [alertPrefs, setAlertPrefs] = useState<AlertPrefs>(item.alertPrefs || { inApp: true, email: false, sms: false, push: false });

  // Defect-specific fields
  const [reproSteps, setReproSteps] = useState(item.reproSteps || '');
  const [rootCause, setRootCause] = useState(item.rootCause || '');
  const [environment, setEnvironment] = useState(item.environment || '');
  const [severityImpact, setSeverityImpact] = useState(item.severityImpact || '');

  // Comment form
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // Estimate (local state so modal stays open)
  const [estimating, setEstimating] = useState(false);
  const [localEstimate, setLocalEstimate] = useState<TokenEstimate | null>(item.estimate || null);

  // Reset when item changes
  useEffect(() => {
    setTitle(item.title);
    setDescription(item.description);
    setPriority(item.priority);
    setMoscow(item.moscow || '');
    setStatus(item.status);
    setAssignedAgent(item.assignedAgent || '');
    setAlertPrefs(item.alertPrefs || { inApp: true, email: false, sms: false, push: false });
    setReproSteps(item.reproSteps || '');
    setRootCause(item.rootCause || '');
    setEnvironment(item.environment || '');
    setSeverityImpact(item.severityImpact || '');
    setLocalEstimate(item.estimate || null);
    setNewComment('');
  }, [item]);

  // Load agents for assignment dropdown
  useEffect(() => {
    getAllAgents().then(list => setAgents(list.map(a => ({ id: a.id, name: a.name })))).catch(() => {});
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateBacklogItem(item.id, {
        title, description, priority,
        moscow: moscow || null,
        status, assignedAgent: assignedAgent || null,
        alertPrefs,
        ...(item.type === 'defect' ? {
          reproSteps: reproSteps || null,
          rootCause: rootCause || null,
          environment: environment || null,
          severityImpact: severityImpact || null,
        } : {}),
      } as Partial<BacklogItem>);
      onUpdated();
    } catch { /* silent */ }
    setSaving(false);
  }, [item.id, item.type, title, description, priority, moscow, status, assignedAgent, alertPrefs, reproSteps, rootCause, environment, severityImpact, onUpdated]);

  const handleAddComment = useCallback(async () => {
    if (!newComment.trim()) return;
    setAddingComment(true);
    try {
      await addBacklogComment(item.id, newComment.trim());
      setNewComment('');
      onUpdated();
    } catch { /* silent */ }
    setAddingComment(false);
  }, [item.id, newComment, onUpdated]);

  const handleDiscuss = useCallback(() => {
    if (assignedAgent) {
      navigate(`/chat?agent=${assignedAgent}&context=backlog:${item.id}`);
      onClose();
    }
  }, [assignedAgent, item.id, navigate, onClose]);

  const handleGenerateEstimate = useCallback(async () => {
    setEstimating(true);
    try {
      const result = await generateEstimate(item.id, assignedAgent || undefined);
      setLocalEstimate(result.item.estimate || null);
    } catch { /* silent */ }
    setEstimating(false);
  }, [item.id, assignedAgent]);

  const isDirty = title !== item.title || description !== item.description ||
    priority !== item.priority || (moscow || null) !== (item.moscow || null) ||
    status !== item.status || (assignedAgent || null) !== (item.assignedAgent || null) ||
    JSON.stringify(alertPrefs) !== JSON.stringify(item.alertPrefs || { inApp: true, email: false, sms: false, push: false }) ||
    (item.type === 'defect' && (
      (reproSteps || null) !== (item.reproSteps || null) ||
      (rootCause || null) !== (item.rootCause || null) ||
      (environment || null) !== (item.environment || null) ||
      (severityImpact || null) !== (item.severityImpact || null)
    ));

  const footer = (
    <>
      {assignedAgent && (
        <Button variant="secondary" size="sm" onClick={handleDiscuss}>
          <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
          Discuss with Agent
        </Button>
      )}
      <div className="flex-1" />
      <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
      <Button variant="primary" size="sm" onClick={handleSave} disabled={!isDirty || saving} isLoading={saving}>
        Save Changes
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${item.id}: ${item.title}`} size="lg" footer={footer}>
      <div className="space-y-5">
        {/* Top row: ID, type, labels */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono text-gray-400">{item.id}</span>
          <Badge variant={item.type === 'defect' ? 'danger' : 'info'}>
            {item.type === 'defect' ? 'Bug' : 'Request'}
          </Badge>
          {item.labels.map(l => (
            <span key={l} className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded">{l}</span>
          ))}
          <span className="text-[10px] text-gray-400">by {item.reportedBy} &middot; {new Date(item.createdAt).toLocaleDateString()}</span>
        </div>

        {/* Title */}
        <div>
          <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Title</label>
          <input
            type="text" value={title} onChange={e => setTitle(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Description</label>
          <textarea
            value={description} onChange={e => setDescription(e.target.value)} rows={4}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 resize-y"
          />
        </div>

        {/* Defect Details — only shown for defect-type items */}
        {item.type === 'defect' && (
          <fieldset className="bg-red-50 border-l-4 border-red-500 rounded-md p-4">
            <legend className="text-[10px] font-semibold text-red-700 uppercase tracking-wide px-1">Defect Details</legend>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1" htmlFor="reproSteps">
                  Steps to Reproduce
                </label>
                <textarea
                  id="reproSteps"
                  aria-label="Steps to reproduce the defect"
                  value={reproSteps}
                  onChange={e => setReproSteps(e.target.value)}
                  rows={5}
                  maxLength={2000}
                  placeholder={"1. Navigate to...\n2. Click on...\n3. Observe..."}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 resize-y"
                />
                <span className={`text-[10px] mt-0.5 block ${reproSteps.length > 1900 ? 'text-red-500' : reproSteps.length > 1600 ? 'text-yellow-600' : 'text-gray-400'}`}>
                  {reproSteps.length} / 2000
                </span>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1" htmlFor="rootCause">
                  Root Cause
                </label>
                <textarea
                  id="rootCause"
                  aria-label="Root cause analysis"
                  value={rootCause}
                  onChange={e => setRootCause(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Describe the underlying cause of the defect..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 resize-y"
                />
                <span className={`text-[10px] mt-0.5 block ${rootCause.length > 950 ? 'text-red-500' : rootCause.length > 800 ? 'text-yellow-600' : 'text-gray-400'}`}>
                  {rootCause.length} / 1000
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1" htmlFor="environment">
                    Environment
                  </label>
                  <input
                    id="environment"
                    type="text"
                    aria-label="Environment where defect occurs"
                    value={environment}
                    onChange={e => setEnvironment(e.target.value)}
                    maxLength={200}
                    placeholder="Browser, OS, device, app version..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1" htmlFor="severityImpact">
                    Severity Impact
                  </label>
                  <select
                    id="severityImpact"
                    aria-label="Severity impact assessment"
                    value={severityImpact}
                    onChange={e => setSeverityImpact(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
                  >
                    <option value="">Not assessed</option>
                    <option value="blocks_all">Blocks all users</option>
                    <option value="blocks_some">Blocks some users</option>
                    <option value="data_loss">Data loss risk</option>
                    <option value="performance">Performance</option>
                    <option value="cosmetic">Cosmetic</option>
                  </select>
                </div>
              </div>
            </div>
          </fieldset>
        )}

        {/* Priority + MoSCoW + Status + Agent -- 4-column grid */}
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Priority</label>
            <select value={priority} onChange={e => setPriority(e.target.value as BacklogPriority)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50">
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">MoSCoW</label>
            <select value={moscow} onChange={e => setMoscow(e.target.value as BacklogMoscow | '')}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50">
              <option value="">Not set</option>
              <option value="must">Must Have</option>
              <option value="should">Should Have</option>
              <option value="could">Could Have</option>
              <option value="wont">Won't Have</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value as BacklogStatus)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50">
              <option value="new">New</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="review">Review</option>
              <option value="done">Done</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Assigned To</label>
            <select value={assignedAgent} onChange={e => setAssignedAgent(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50">
              <option value="">Unassigned</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        </div>

        {/* MoSCoW display badge if set */}
        {moscow && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400">MoSCoW:</span>
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
              moscow === 'must' ? 'bg-red-100 text-red-700' :
              moscow === 'should' ? 'bg-orange-100 text-orange-700' :
              moscow === 'could' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-600'
            }`}>{moscowLabels[moscow]}</span>
            <span className="text-[10px] text-gray-400 ml-2">Priority:</span>
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${priorityColors[priority]}`}>{priority}</span>
          </div>
        )}

        {/* Alert Preferences */}
        <div>
          <label className="block text-[10px] font-medium text-gray-500 uppercase mb-2">Alert Preferences</label>
          <div className="flex gap-4">
            {(['inApp', 'email', 'sms', 'push'] as const).map(key => (
              <label key={key} className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={alertPrefs[key]}
                  onChange={e => setAlertPrefs(prev => ({ ...prev, [key]: e.target.checked }))}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/50"
                />
                {key === 'inApp' ? 'In-App' : key === 'sms' ? 'SMS' : key.charAt(0).toUpperCase() + key.slice(1)}
              </label>
            ))}
          </div>
        </div>

        {/* Token Estimate */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-[10px] font-medium text-gray-500 uppercase">Token Estimate</label>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleGenerateEstimate}
              disabled={estimating}
              isLoading={estimating}
            >
              {localEstimate ? 'Re-estimate' : 'Generate Estimate'}
            </Button>
          </div>
          {localEstimate ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <span className="block text-[10px] text-gray-400">Cost</span>
                  <span className="text-sm font-semibold text-ldaja-black">${localEstimate.estimatedCost.toFixed(2)}</span>
                </div>
                <div>
                  <span className="block text-[10px] text-gray-400">Tokens</span>
                  <span className="text-sm font-medium text-gray-700">
                    {(localEstimate.inputTokens + localEstimate.outputTokens).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="block text-[10px] text-gray-400">Duration</span>
                  <span className="text-sm font-medium text-gray-700">~{localEstimate.estimatedDuration} min</span>
                </div>
                <div>
                  <span className="block text-[10px] text-gray-400">Confidence</span>
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                    localEstimate.confidence === 'high' ? 'bg-green-100 text-green-700' :
                    localEstimate.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{localEstimate.confidence}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-1 border-t border-gray-100">
                <span className="text-[10px] text-gray-400">
                  Class: <span className="text-gray-600 font-medium">{localEstimate.taskClass}</span>
                </span>
                <span className="text-[10px] text-gray-400">
                  Model: <span className="text-gray-600">{localEstimate.model}</span>
                </span>
                <span className="text-[10px] text-gray-400">
                  By: <span className="text-gray-600">{localEstimate.estimatedBy}</span>
                </span>
                <span className="text-[10px] text-gray-400">
                  {new Date(localEstimate.estimatedAt).toLocaleDateString()}
                </span>
              </div>
              <p className="text-[10px] text-gray-400 italic">{localEstimate.reasoning}</p>
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No estimate yet. Click "Generate Estimate" to predict token usage and cost.</p>
          )}
        </div>

        {/* Comments */}
        <div>
          <label className="block text-[10px] font-medium text-gray-500 uppercase mb-2">
            Comments ({item.comments.length})
          </label>
          {item.comments.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No comments yet</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
              {item.comments.map(c => (
                <div key={c.id} className="p-2.5 bg-gray-50 rounded-md border border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-ldaja-black">{c.author}</span>
                    <span className="text-[10px] text-gray-400">{new Date(c.createdAt).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-gray-600">{c.content}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Add a comment..."
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
              className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
            />
            <Button variant="secondary" size="sm" onClick={handleAddComment}
              disabled={!newComment.trim() || addingComment} isLoading={addingComment}>
              Post
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
